import React, { useState } from 'react';
import { ClaimDocumentUpload } from './ClaimDocumentUpload';

type SubmittedDocument = {
  intimationDocumentId: string;
  documentName: string;
  permanentFile: string;
  fileUrl: string;
  createdOn: string;
};

type SubmittedIntimation = {
  id: number;
  memberId: string;
  mobileNo: string;
  emailId: string;
  claimDateOfAdmission: string;
  claimDateOfDischarge: string;
  hospName: string;
  hospAddress: string;
  reasonForHospitalization: string;
  disease: string;
  claimAmount: number;
  claimType: string;
  policyId: number;
  employeeId: string;
  remarks: string | null;
  documents?: SubmittedDocument[];
};

type Props = {
  items: SubmittedIntimation[];
  memberList: { id: string; name: string; relation: string }[];
  employeeId: string | undefined;
  onDocumentSaveSuccess?: () => void;
};

export const SubmittedIntimationList: React.FC<Props> = ({ items, memberList, employeeId, onDocumentSaveSuccess }) => {
  const [selectedIntimation, setSelectedIntimation] = useState<SubmittedIntimation | null>(null);

  const getMemberName = (memberId: string) => {
    const member = memberList.find((m) => m.id === memberId);
    return member ? `${member.name} (${member.relation})` : memberId;
  };

  if (!items || items.length === 0) {
    return <div className='text-muted'>No submitted intimation details found.</div>;
  }

  return (
    <>
      {items.map((item) => (
        <div
          className='d-flex flex-column shadow-sm bg-light-primary rounded p-5 mb-7'
          key={item.id}
        >
          <div className='fw-bolder text-black text-hover-primary fs-6 mb-2'>
            {item.claimType} Intimation - #{item.id}
          </div>
          <div className='d-flex flex-column'>
            <div className='d-flex mb-1'>
              <span className='fw-bold text-dark me-3' style={{ minWidth: '180px' }}>
                Member:
              </span>
              <span className='text-gray-700'>{getMemberName(item.memberId)}</span>
            </div>
            <div className='d-flex mb-1'>
              <span className='fw-bold text-dark me-3' style={{ minWidth: '180px' }}>
                Mobile No:
              </span>
              <span className='text-gray-700'>{item.mobileNo}</span>
            </div>
            <div className='d-flex mb-1'>
              <span className='fw-bold text-dark me-3' style={{ minWidth: '180px' }}>
                Email:
              </span>
              <span className='text-gray-700'>{item.emailId}</span>
            </div>
            <div className='d-flex mb-1'>
              <span className='fw-bold text-dark me-3' style={{ minWidth: '180px' }}>
                Date of Admission:
              </span>
              <span className='text-gray-700'>{item.claimDateOfAdmission}</span>
            </div>
            <div className='d-flex mb-1'>
              <span className='fw-bold text-dark me-3' style={{ minWidth: '180px' }}>
                Date of Discharge:
              </span>
              <span className='text-gray-700'>{item.claimDateOfDischarge}</span>
            </div>
            <div className='d-flex mb-1'>
              <span className='fw-bold text-dark me-3' style={{ minWidth: '180px' }}>
                Hospital Name:
              </span>
              <span className='text-gray-700'>{item.hospName}</span>
            </div>
            <div className='d-flex mb-1'>
              <span className='fw-bold text-dark me-3' style={{ minWidth: '180px' }}>
                Hospital Address:
              </span>
              <span className='text-gray-700'>{item.hospAddress}</span>
            </div>
            <div className='d-flex mb-1'>
              <span className='fw-bold text-dark me-3' style={{ minWidth: '180px' }}>
                Reason for Hospitalization:
              </span>
              <span className='text-gray-700'>{item.reasonForHospitalization}</span>
            </div>
            <div className='d-flex mb-1'>
              <span className='fw-bold text-dark me-3' style={{ minWidth: '180px' }}>
                Disease:
              </span>
              <span className='text-gray-700'>{item.disease}</span>
            </div>
            <div className='d-flex mb-1'>
              <span className='fw-bold text-dark me-3' style={{ minWidth: '180px' }}>
                Claim Amount:
              </span>
              <span className='text-gray-700'>{item.claimAmount}</span>
            </div>
            {item.remarks && (
              <div className='d-flex mb-1'>
                <span className='fw-bold text-dark me-3' style={{ minWidth: '180px' }}>
                  Remarks:
                </span>
                <span className='text-gray-700'>{item.remarks}</span>
              </div>
            )}
            {item.documents && item.documents.length > 0 && (
              <div className='mt-4'>
                <div className='fw-bold text-dark mb-2'>Uploaded Documents:</div>
                <div className='d-flex flex-column'>
                  {item.documents.map((doc) => (
                    <a
                      key={doc.intimationDocumentId}
                      href={doc.permanentFile}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-primary text-hover-primary text-decoration-underline mb-1'
                    >
                      {doc.documentName}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className='mt-4 text-end'>
            <button
              type='button'
              className='btn btn-sm btn-primary'
              onClick={() => setSelectedIntimation(item)}
            >
              Upload Documents
            </button>
          </div>
        </div>
      ))}

      {selectedIntimation && (
        <ClaimDocumentUpload
          show={!!selectedIntimation}
          handleClose={() => setSelectedIntimation(null)}
          onSaveSuccess={onDocumentSaveSuccess}
          intimationId={selectedIntimation.id}
          memberId={selectedIntimation.memberId}
          employeeId={employeeId}
        />
      )}
    </>
  );
};
